import {
  assessJob,
  deduplicationRatio,
  groupByVault,
  humanBytes,
  machineNamesForRepo,
  needsAttention,
  percent,
  readVaultStorageTier,
  rollingVerifyTierOf,
  sharedByComputersSentence,
  shortId,
  storedBytesOf,
  storageTierLine,
  vaultAttentionReason,
  vaultCheckFor,
  vaultCheckLine,
  type BackupJob,
  type HealthAssessment,
  type PortalOverview,
} from "@bigbear/shared";
import { useState } from "react";
import { Link } from "react-router-dom";

import { HealthBadge } from "../components/HealthBadge";
import { JobActionsMenu } from "../components/JobActionsMenu";
import { StatTile } from "../components/StatTile";
import { NoDevices } from "./NoDevices";

interface Props {
  overview: PortalOverview;
  /** Injectable so tests are not at the mercy of the wall clock. */
  nowUtc?: number;
  /**
   * Absent in the handful of places (unit tests, mainly) that render this
   * page with nothing to act through. Every real caller has a session or a
   * synthetic stand-in to remove a machine with, so the button below only
   * needs to exist when this does.
   */
  onRemoveMachine?: (machineId: string) => Promise<void>;
  /** Same convention as {@link onRemoveMachine}: absent means nothing to act through. */
  onClearJobHistory?: (machineId: string, jobId: string) => Promise<void>;
}

interface AssessedJob {
  job: BackupJob;
  assessment: HealthAssessment;
}

/**
 * Sum of the values that were actually reported, or null if none were.
 *
 * Skipping absent values rather than treating them as zero keeps a total
 * honest when only some repositories have reported: "3 GB across the ones we
 * know about" is useful, and silently counting the unknown ones as empty would
 * understate it without saying so.
 */
function total(values: readonly (number | undefined)[]): number | null {
  const known = values.filter((value): value is number => value !== undefined);
  return known.length === 0 ? null : known.reduce((sum, value) => sum + value, 0);
}

export function Overview({ overview, nowUtc, onRemoveMachine, onClearJobHistory }: Props) {
  const now = nowUtc ?? Math.floor(Date.now() / 1000);
  const { repositories, machines, jobs, snapshots } = overview;

  const assessed: AssessedJob[] = jobs.map((job) => ({
    job,
    assessment: assessJob(job, snapshots, now),
  }));

  // The reason this page exists. Anything not healthy is surfaced above the
  // inventory, because a problem found by scrolling is a problem found late.
  const attention = assessed.filter(({ assessment }) => needsAttention(assessment.health));

  // Both are optional on the model. A backup never measures the stored size;
  // a vault's scheduled check does when it lists the vault (ADR 0028), so
  // Stored is the vaults' sizes, once per vault, by the summary email's own
  // rule. A repository that has told us nothing contributes nothing, and a
  // total nobody could measure stays unmeasured rather than becoming a zero
  // that would render as "100% saved by deduplication".
  const logicalBytes = total(repositories.map((r) => r.logicalBytes));
  const storedBytes = storedBytesOf(overview);
  const saved =
    logicalBytes === null || storedBytes === null
      ? null
      : deduplicationRatio(logicalBytes, storedBytes);

  // Nothing has ever reported: the account is new, or no machine has been
  // connected to it. That needs instructions rather than an empty inventory,
  // and it is a different situation from a connected machine that has a
  // repository yet to back up to.
  if (machines.length === 0) {
    return <NoDevices />;
  }

  if (repositories.length === 0) {
    return (
      <div className="empty">
        <h2>No vaults yet</h2>
        <p className="muted">
          Create one in the desktop app and it will appear here after its first
          backup.
        </p>
      </div>
    );
  }

  return (
    <>
      <section className="tiles">
        <StatTile
          label="Machines"
          value={String(machines.length)}
          detail={`${jobs.length} job${jobs.length === 1 ? "" : "s"}`}
        />
        <StatTile
          label="Protected"
          value={logicalBytes === null ? "—" : humanBytes(logicalBytes)}
          detail="across all vaults"
        />
        <StatTile
          label="Stored"
          value={storedBytes === null ? "—" : humanBytes(storedBytes)}
          {...(saved === null
            ? { detail: "not yet measured" }
            : { detail: `${percent(saved)} saved by deduplication` })}
        />
        <StatTile
          label="Needs attention"
          value={String(attention.length)}
          detail={attention.length === 0 ? "everything is current" : "see below"}
        />
      </section>

      <section className="panel">
        <h2>Vaults</h2>
        <ul className="job-list">
          {vaultGroups(overview, now).map((group) => (
            <li className={group.attention ? "job job--attention" : "job"} key={group.key}>
              <div className="job__main">
                <strong>{group.label}</strong>
                {group.destination && <span className="muted"> · {group.destination}</span>}
                {group.storageTier && <p className="muted">{group.storageTier}</p>}
                {group.check && <p className="job__reason">{group.check}</p>}
                {group.attention && <p className="job__reason">{group.attention}</p>}
                {group.sharedSentence && <p className="job__reason">{group.sharedSentence}</p>}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="panel">
        <h2>Needs attention</h2>
        {attention.length === 0 ? (
          <p className="muted">
            Every job has run recently and finished cleanly.
          </p>
        ) : (
          <ul className="job-list">
            {attention.map(({ job, assessment }) => (
              <JobRow
                key={`${job.machineId}/${job.id}`}
                job={job}
                assessment={assessment}
                machineName={nameOf(overview, job.machineId)}
                showMachine
                onClearHistory={onClearJobHistory}
              />
            ))}
          </ul>
        )}
      </section>

      {machines.map((machine) => {
        const mine = assessed.filter(({ job }) => job.machineId === machine.id);
        return (
          <section className="panel" key={machine.id}>
            <div className="panel__header">
              <h2>
                {/* Shortened, because a device id is now a generated UUID
                    rather than the hostname it used to be. It is here only to
                    separate two machines a user has given the same name; the
                    full thirty-six characters beside every heading is noise. */}
                {machine.name} <span className="muted mono">{shortId(machine.id, 8)}</span>
              </h2>
              {onRemoveMachine && (
                <RemoveMachineButton machineId={machine.id} onRemove={onRemoveMachine} />
              )}
            </div>
            {mine.length === 0 ? (
              <p className="muted">No jobs configured on this machine.</p>
            ) : (
              <ul className="job-list">
                {mine.map(({ job, assessment }) => (
                  <JobRow
                    key={job.id}
                    job={job}
                    assessment={assessment}
                    onClearHistory={onClearJobHistory}
                  />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </>
  );
}

function nameOf(overview: PortalOverview, machineId: string): string {
  return overview.machines.find((m) => m.id === machineId)?.name ?? machineId;
}

interface VaultGroup {
  /** `repoId` when every row in the group agrees on one, else the one row's
   * own id — the two are never mixed, so this is stable across renders. */
  key: string;
  label: string;
  destination?: string;
  /** The provider and storage class, as `storageTierLine` names them, from
   * the group's first row that reported a provider; absent where none did,
   * which says nothing rather than guessing. */
  storageTier?: string;
  /** The vault's Scheduled check line, in the summary email's words
   * (`vaultCheckLine`); absent for a vault no device reported one for. */
  check?: string;
  /** Why the vault needs attention (`vaultAttentionReason`), if it does. */
  attention?: string;
  /** Null for a vault only one row reports, which includes every row with no
   * `repoId` at all: that is "has not said" (`#308`), not "confirmed alone",
   * and the row is shown exactly as it always has been rather than as
   * "not shared". */
  sharedSentence: string | null;
}

/**
 * `overview.repositories`, one row per vault rather than one per device.
 *
 * Rows sharing a `repoId` collapse into the group's first row's label and
 * destination — the account's devices commonly agree on both — and the first
 * storage tier any of them reported. A row without a `repoId` never merges
 * with another, so it always stands alone as "as it is today" without a
 * sharing sentence.
 *
 * Dates on the check line are in the reader's own zone.
 */
function vaultGroups(overview: PortalOverview, nowUtc: number): VaultGroup[] {
  return groupByVault(overview.repositories).map((rows) => {
    const repo = rows[0]!;
    const names = repo.repoId
      ? machineNamesForRepo(overview.repositories, overview.machines, repo.repoId)
      : [nameOf(overview, repo.machineId)];

    // A device too old to report a provider says nothing about it, so a
    // shared vault's line comes from whichever of its rows did.
    const reporting = repo.repoId ? rows.find((r) => r.provider !== undefined) : repo;
    const tier = readVaultStorageTier(reporting?.provider, reporting?.storageClass);

    const vault = vaultCheckFor(overview.vaults, rows.map((r) => r.id));
    const check = vault && vaultCheckLine(vault, rollingVerifyTierOf(rows), nowUtc, undefined);
    const attention = vault && vaultAttentionReason(vault, nowUtc, undefined);

    return {
      key: repo.repoId ?? repo.id,
      label: repo.label,
      ...(repo.destination === undefined ? {} : { destination: repo.destination }),
      ...(tier === null ? {} : { storageTier: storageTierLine(tier) }),
      ...(check ? { check } : {}),
      ...(attention ? { attention } : {}),
      sharedSentence: sharedByComputersSentence(names),
    };
  });
}

/**
 * "Remove this machine", for a device that will never report again.
 *
 * Two clicks rather than one: this deletes the machine's history from the
 * portal and signs it out of reporting, and a still-installed machine can
 * only clear its own stale entries by reporting again — not by anything
 * reversible in this UI.
 */
function RemoveMachineButton({
  machineId,
  onRemove,
}: {
  machineId: string;
  onRemove: (machineId: string) => Promise<void>;
}) {
  const [state, setState] = useState<"idle" | "confirming" | "removing" | "failed">("idle");
  const [error, setError] = useState<string | null>(null);

  if (state === "idle") {
    return (
      <button type="button" className="button button--small" onClick={() => setState("confirming")}>
        Remove this machine
      </button>
    );
  }

  if (state === "confirming") {
    return (
      <span className="machine-remove__confirm">
        Remove this machine? Its history leaves the portal and it's signed out of reporting.
        <button
          type="button"
          className="button button--small button--danger"
          onClick={async () => {
            setState("removing");
            try {
              await onRemove(machineId);
              // No `setState` on success: `onRemove` reloads the overview,
              // which unmounts this machine's whole section along with it.
            } catch (cause) {
              setError(cause instanceof Error ? cause.message : "Could not remove this machine.");
              setState("failed");
            }
          }}
        >
          Confirm
        </button>
        <button type="button" className="button button--small" onClick={() => setState("idle")}>
          Cancel
        </button>
      </span>
    );
  }

  if (state === "removing") {
    return <span className="muted">Removing…</span>;
  }

  return (
    <span className="machine-remove__confirm">
      <span className="muted">{error}</span>
      <button type="button" className="button button--small" onClick={() => setState("idle")}>
        Try again
      </button>
    </span>
  );
}

function JobRow({
  job,
  assessment,
  machineName,
  showMachine = false,
  onClearHistory,
}: {
  job: BackupJob;
  assessment: HealthAssessment;
  machineName?: string;
  showMachine?: boolean;
  onClearHistory?: ((machineId: string, jobId: string) => Promise<void>) | undefined;
}) {
  return (
    <li className={needsAttention(assessment.health) ? "job job--attention" : "job"}>
      <div className="job__main">
        <Link to={`jobs/${encodeURIComponent(job.machineId)}/${encodeURIComponent(job.id)}`}>
          {job.name}
        </Link>
        {showMachine && machineName && (
          <span className="muted"> · {machineName}</span>
        )}
        <p className="job__reason">{assessment.reason}</p>
      </div>
      <div className="job__actions">
        <HealthBadge health={assessment.health} />
        {onClearHistory && (
          <JobActionsMenu
            machineId={job.machineId}
            jobId={job.id}
            label={job.name}
            onClearHistory={onClearHistory}
          />
        )}
      </div>
    </li>
  );
}
